import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { founderService } from '../services/accelerators/founders/api_MyFounders';
import { progressService } from '../services/api_progress';
import { LEXICON_FIELD_TO_NARRATIVE_MODULE } from '../services/api_subscriptions';
import type { ModuleId } from '../types';

interface NarrativeWrapperProps {
  moduleId: ModuleId;
  fieldName: string;
  children: (props: {
    initialValue: string;
    onComplete: (content: string) => void;
    founderCategory?: string;
    backToLexiconButton: React.ReactNode | null;
  }) => React.ReactNode;
}

const NarrativeWrapper: React.FC<NarrativeWrapperProps> = ({
  moduleId, fieldName, children
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locale } = useParams();
  const { t } = useTranslation('common');
  const profileId = searchParams.get('profileId');
  // Prefer the clicked field from the lightbulb, but only when it is a known
  // Lexicon field that belongs to THIS module; otherwise fall back to the
  // route's hardcoded representative field. This fixes shared-module siblings
  // and prevents a crafted ?field= from targeting an arbitrary attribute.
  const queryField = searchParams.get('field');
  const effectiveField =
    queryField && LEXICON_FIELD_TO_NARRATIVE_MODULE[queryField] === moduleId
      ? queryField
      : fieldName;
  const [initialValue, setInitialValue] = useState('');
  const [loading, setLoading] = useState(!!profileId);

  useEffect(() => {
    if (!profileId || profileId.startsWith('example')) {
      setLoading(false);
      return;
    }
    founderService.getSubmissionProfile(profileId).then(res => {
      if (res.success && res.data) {
        setInitialValue((res.data as any)[effectiveField] || '');
      }
    }).finally(() => setLoading(false));
  }, [profileId, effectiveField]);

  const handleComplete = async (content: string) => {
    if (profileId && !profileId.startsWith('example')) {
      try {
        await founderService.updateSubmissionProfile(
          profileId,
          { [effectiveField]: content } as any
        );
      } catch (err) {
        console.error('Failed to save narrative to Lexicon:', err);
      }
    }

    try {
      await progressService.completeStep(moduleId, 'narrative_complete');
    } catch (err) {
      console.error('Failed to record narrative completion:', err);
    }

    if (profileId) {
      navigate(`/${locale}/founders/my-founder-lexicon`);
    }
  };

  const backToLexiconButton = profileId ? (
    <div className="flex justify-center mt-8 mb-4">
      <button
        onClick={() => navigate(`/${locale}/founders/my-founder-lexicon`)}
        className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl font-medium transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        {t('backToLexicon')}
      </button>
    </div>
  ) : null;

  if (loading) return <div>Loading...</div>;

  return <>{children({ initialValue, onComplete: handleComplete, backToLexiconButton })}</>;
};

export default NarrativeWrapper;
